/**
 * 分拣批次 mock 派生数据（读侧），逐函数转写自产品交互原型：
 * prog / split / bagPlan → deriveBagPlan、deriveProgress；bagList → listBatchBags；curLog → deriveBatchLogs；
 * drill → deriveLoadDetail；moveGroups / curBatchOf → previewMoveIn；outItems → previewMoveOut。
 *
 * 真实数据应由后端计算返回，前端仅渲染。
 * todo batch mock: 待后端提供 Query sortingBatches / sortingBatchBags / sortingBatchOperationLogs /
 * sortingBatchLoadDetail / previewSortingBatchMoveIn / previewSortingBatchMoveOut，接入后删除本文件。
 */
import { BagStatus } from '../../bags/graphql/types/BagTagType';
import {
    BAGS_PER_TRUCK, CLOSED_STATUSES, FREEZE_HOURS, LB_PER_PARCEL, MOVE_IN_BLOCKED_STATUSES,
} from '../constants';
import {
    BatchBag, BatchDirection, BatchLog,
    BatchProgress, BatchRowView, BatchStage, BatchStatus, LoadDetail, LoadDetailBag, LoadDetailTruck, MoveInGroup,
    MoveInOrphan, MoveInPreview, MoveInputItem, MoveOutItem, SortingBatch,
} from '../types';
import { isBagLikeNumber } from '../utils/numberInputUtils';
import { codesAt, defaultSegFor } from '../utils/sortCodeUtils';
import { MOCK_OPERATORS, otherHubsOf } from './batchMock';
import { movedOutKey, SortingBatchMockState } from './mockState';

const HOUR = 3600 * 1000;
const MINUTE = 60 * 1000;
const SECOND = 1000;

// ---------------------------------------------------------------- 阶段拆分

/** 按比例拆分整数，余数按小数部分从大到小补齐（原型 split） */
export const splitByRatios = (total: number, ratios: number[]): number[] => {
    if (!total || !ratios.length) return ratios.map(() => 0);
    const raw = ratios.map((r) => total * r);
    const out = raw.map((x) => Math.floor(x));
    let rest = total - out.reduce((a, x) => a + x, 0);
    const order = raw.map((x, i) => ({ i, f: x - Math.floor(x) })).sort((a, b) => b.f - a.f);
    for (let k = 0; rest > 0; k += 1, rest -= 1) out[order[k % order.length].i] += 1;
    return out;
};

/** 各状态下包牌在 5 个阶段的分布比例（原型 prog） */
const STAGE_RATIOS: Record<BatchStatus, number[]> = {
    CREATED: [1, 0, 0, 0, 0],
    CANCELLED: [1, 0, 0, 0, 0],
    SORTING: [0.62, 0.16, 0.12, 0.1, 0],
    SORTING_ENDED: [0, 0.22, 0.16, 0.27, 0.35],
    COMPLETED: [0, 0, 0, 0, 1],
};

const stageRatiosOf = (b: SortingBatch): number[] => {
    let f = [...STAGE_RATIOS[b.status]];
    const v = (((b.id || 0) % 3) - 1) * 0.05;
    if (f[4] > 0 && f[4] < 1) f = [f[0], f[1], Math.max(0, f[2] + v), Math.max(0, f[3] - v), f[4]];
    const sum = f.reduce((a, x) => a + x, 0);
    return sum ? f.map((x) => x / sum) : f;
};

interface PlannedBag {
    /** 批次内序号（0 起） */
    index: number;
    stage: BatchStage;
    parcels: number;
    packed: boolean;
    truckNo?: string;
}

interface PlannedTruck {
    truckNo: string;
    plate: string;
    index: number;
    departed: boolean;
}

export interface BagPlanResult {
    stageBags: number[];
    stageParcels: number[];
    bags: PlannedBag[];
    trucks: PlannedTruck[];
}

/**
 * 种子包牌的阶段分布与车次（原型 bagPlan）。已装包包牌按比例分到 5 个阶段，
 * 未使用包牌（已创建 - 已装包）计入「已装包未发运」阶段且编号排在最后，与详情包牌列表编号一致。
 */
export const deriveBagPlan = (b: SortingBatch, seedBagCount = b.createdBags): BagPlanResult => {
    const ratios = stageRatiosOf(b);
    const packed = Math.min(b.packedBags || 0, seedBagCount);
    const packedSplit = splitByRatios(packed, ratios);
    const parcelSplit = splitByRatios(b.parcels || 0, ratios);
    const bags: PlannedBag[] = [];
    let idx = 0;
    packedSplit.forEach((cnt, k) => {
        const per = splitByRatios(parcelSplit[k], Array.from({ length: cnt }, () => 1 / Math.max(cnt, 1)));
        for (let j = 0; j < cnt; j += 1) {
            bags.push({ index: idx, stage: k as BatchStage, parcels: per[j] || 0, packed: true });
            idx += 1;
        }
    });
    for (; idx < seedBagCount; idx += 1) bags.push({ index: idx, stage: 0, parcels: 0, packed: false });

    const trucks: PlannedTruck[] = [];
    const chunk = (arr: PlannedBag[], departed: boolean) => {
        for (let s = 0; s < arr.length; s += BAGS_PER_TRUCK) {
            const truckNo = `TRK${String(b.id).slice(-2)}${String(trucks.length + 1).padStart(2, '0')}`;
            arr.slice(s, s + BAGS_PER_TRUCK).forEach((x) => { x.truckNo = truckNo; });
            trucks.push({ truckNo, plate: `CA-${4210 + trucks.length * 7}-T`, index: trucks.length, departed });
        }
    };
    chunk([...bags.filter((x) => x.stage === 4), ...bags.filter((x) => x.stage === 3)], true);
    chunk(bags.filter((x) => x.stage === 2), false);

    const stageBags = [0, 1, 2, 3, 4].map((k) => bags.filter((x) => x.stage === k).length);
    return { stageBags, stageParcels: parcelSplit, bags, trucks };
};

const truckCount = (plan: BagPlanResult, stage: BatchStage): number =>
    new Set(plan.bags.filter((x) => x.stage === stage && x.truckNo).map((x) => x.truckNo)).size;

/** 阶段进度（列表 5 个阶段列）；本会话新建的包牌计入「已装包未发运」 */
export const deriveProgress = (state: SortingBatchMockState, b: SortingBatch): BatchProgress => {
    const extra = (state.extraBags[b.id] || []).length;
    const plan = deriveBagPlan(b, Math.max(0, b.createdBags - extra));
    const stages = [0, 1, 2, 3, 4].map((k) => ({
        bags: plan.stageBags[k] + (k === 0 ? extra : 0),
        parcels: plan.stageParcels[k],
        trucks: k >= 2 ? truckCount(plan, k as BatchStage) : 0,
    }));
    return { totalBags: b.createdBags, stages };
};

// ---------------------------------------------------------------- 停更与列表行

export const frozenAtOf = (b: SortingBatch, now: number): number | undefined => {
    // 停更展示暂时关闭，对齐后端 SortingBatchCompletionApplication 的停更标记停用（同一次改动）
    void b;
    void now;
    void FREEZE_HOURS;
    return undefined;
};

export const toRowView = (state: SortingBatchMockState, b: SortingBatch, now: number): BatchRowView => {
    const frozenAt = frozenAtOf(b, now);
    return { ...b, frozen: frozenAt !== undefined, frozenAt, progress: deriveProgress(state, b) };
};

export const batchesOf = (state: SortingBatchMockState, direction: BatchDirection): SortingBatch[] =>
    (direction === 'INBOUND' ? state.inbound : state.outbound);

export const findBatchByNumber = (state: SortingBatchMockState, batchNumber: string): SortingBatch | undefined =>
    state.outbound.find((b) => b.batchNumber === batchNumber) || state.inbound.find((b) => b.batchNumber === batchNumber);

// ---------------------------------------------------------------- 包牌

export const makeBagNumber = (batchId: number, index: number): string => `BAG${String(batchId * 1000 + index + 1).padStart(13, '0')}`;

/** 批次包牌分拣码池（目的地默认段数） */
export const bagCodePool = (state: SortingBatchMockState, b: SortingBatch): string[] =>
    codesAt(b.destination, defaultSegFor(b.destination), state.sortCodes);

const plannedBagStatus = (x: PlannedBag): BagStatus => {
    if (!x.packed) return BagStatus.NOT_USED;
    if (x.stage > 0) return BagStatus.CLOSED;
    return x.index % 3 === 0 ? BagStatus.CLOSED : BagStatus.USED;
};

const generatedBags = (state: SortingBatchMockState, b: SortingBatch): (BatchBag & { stage: BatchStage; truckNo?: string })[] => {
    const extra = (state.extraBags[b.id] || []).length;
    const plan = deriveBagPlan(b, Math.max(0, b.createdBags - extra));
    const pool = bagCodePool(state, b);
    return plan.bags
        .slice()
        .sort((x, y) => x.index - y.index)
        .map((x) => ({
            bagNumber: makeBagNumber(b.id, x.index),
            sortingCode: pool[x.index % Math.max(pool.length, 1)] || b.destination,
            status: plannedBagStatus(x),
            creationMethod: x.packed && x.index % 2 === 1 ? 'SORTER' : 'MANUAL',
            parcels: x.parcels,
            batchNumber: b.batchNumber,
            stage: x.stage,
            truckNo: x.truckNo,
        }));
};

/** 批次当前包牌列表：种子包牌 + 本会话新建 + 整包移入 − 已移出（原型 bagList）；同一包牌移回原批次时只保留一条 */
export const listBatchBags = (state: SortingBatchMockState, b: SortingBatch): BatchBag[] => {
    const base: BatchBag[] = generatedBags(state, b).map(({ stage, truckNo, ...bag }) => bag);
    const all = [...base, ...(state.extraBags[b.id] || []), ...(state.movedInBags[b.id] || [])];
    const seen = new Set<string>();
    return all.filter((g) => {
        if (state.movedOutBags[movedOutKey(b.batchNumber, g.bagNumber)] || seen.has(g.bagNumber)) return false;
        seen.add(g.bagNumber);
        return true;
    });
};

export const bagSummaryOf = (bags: BatchBag[]): { created: number; used: number; unused: number } => {
    const unused = bags.filter((g) => g.status === BagStatus.NOT_USED).length;
    return { created: bags.length, used: bags.length - unused, unused };
};

// ---------------------------------------------------------------- 操作日志

const capNow = (time: number, now: number): number => Math.min(time, now - MINUTE);

/** 批次操作历史（原型 curLog）：状态事件 + 示例移动事件 + 本会话日志，时间倒序 */
export const deriveBatchLogs = (state: SortingBatchMockState, b: SortingBatch, now: number): BatchLog[] => {
    const hub = `${state.hubPrefix} Hub`;
    const whs = `${String(b.destination || '').split('-').slice(-1)[0]} Warehouse`;
    // 状态事件不早于批次创建（刚新建即结束 / 取消时，capNow 会早于创建时间）
    const at = (h: number) => Math.max(b.createdAt, capNow(b.createdAt + h * HOUR, now));
    const mgr = { operator: MOCK_OPERATORS.hubManager, operatorRole: 'HUB_MANAGER' as const, site: hub };
    const created: BatchLog = b.creationMethod === 'SORTER'
        ? { batchNumber: b.batchNumber, type: 'CREATED', time: b.createdAt, sorterNo: `${state.hubPrefix}-SRT-01`, site: hub }
        : { batchNumber: b.batchNumber, type: 'CREATED', time: b.createdAt, ...mgr };
    const seq: BatchLog[] = [
        created,
        { batchNumber: b.batchNumber, type: 'SORTING', time: at(1), ...mgr },
        { batchNumber: b.batchNumber, type: 'SORTING_ENDED', time: at(3), ...mgr },
        { batchNumber: b.batchNumber, type: 'COMPLETED', time: at(9), operator: MOCK_OPERATORS.whsManager, operatorRole: 'WAREHOUSE_MANAGER', site: whs },
    ];
    const order: BatchStatus[] = ['CREATED', 'SORTING', 'SORTING_ENDED', 'COMPLETED'];
    const upto = b.status === 'CANCELLED' ? 1 : Math.max(0, order.indexOf(b.status));
    // 本会话新建的批次未经分拣，直接结束分拣 / 取消，不出现「分拣中」
    const done = seq.slice(0, upto + 1).filter((e) => !(b.endedBySync && e.type === 'SORTING_ENDED') && !(b.isNew && e.type === 'SORTING'));
    if (b.status === 'CANCELLED') done.push({ batchNumber: b.batchNumber, type: 'CANCELLED', time: at(2), ...mgr });

    const moves: BatchLog[] = [];
    if (upto >= 1 && b.status !== 'CANCELLED' && (b.parcels || 0) > 0 && !b.isNew) {
        // 示例移动只在批次所在的列表（发出 / 接收）内取同流向批次
        const list = state.inbound.some((o) => o.id === b.id) ? state.inbound : state.outbound;
        const same = (o: SortingBatch) => o.destination === b.destination && o.id !== b.id && o.status !== 'CANCELLED';
        const older = list.find((o) => same(o) && o.createdAt < b.createdAt && (o.parcels || 0) > 0);
        const newer = list.find((o) => same(o) && o.createdAt > b.createdAt && !o.isNew);
        if (older) {
            moves.push({
                batchNumber: b.batchNumber, type: 'PARCEL_MOVED_IN', time: at(1) + 42 * SECOND,
                parcelNumber: 'SX10293847513', parcelCount: 3, targetBagNumber: makeBagNumber(b.id, 1), sourceBatch: older.batchNumber, ...mgr,
            });
        }
        if (newer && (upto < 2 || newer.createdAt < at(3))) {
            moves.push({
                batchNumber: b.batchNumber, type: 'BAG_MOVED_OUT', time: capNow(newer.createdAt + 5 * MINUTE, now),
                bagNumber: makeBagNumber(b.id, 3), targetBatch: newer.batchNumber, ...mgr,
            });
        }
    }
    const session = state.sessionLogs.filter((e) => e.batchNumber === b.batchNumber);
    // 状态事件先按发生顺序倒排：同一时刻时后发生的在前（排序稳定）
    return [...done.reverse(), ...moves, ...session].sort((x, y) => y.time - x.time);
};

// ---------------------------------------------------------------- 装载明细

/** 装载明细（原型 drill）：stage 为 null 时取批次全部包牌；本会话新建的包牌与列表一致计入「已装包未发运」 */
export const deriveLoadDetail = (state: SortingBatchMockState, b: SortingBatch, stage: BatchStage | null, now: number): LoadDetail => {
    const extraBags = state.extraBags[b.id] || [];
    const plan = deriveBagPlan(b, Math.max(0, b.createdBags - extraBags.length));
    const gen = [...generatedBags(state, b), ...extraBags.map((g) => ({ ...g, stage: 0 as BatchStage, truckNo: undefined }))];
    const scope = gen
        .filter((g) => stage === null || g.stage === stage)
        .filter((g) => !state.movedOutBags[movedOutKey(b.batchNumber, g.bagNumber)]);
    const toLoadBag = (g: (typeof gen)[number]): LoadDetailBag => {
        const { stage: s, truckNo, ...bag } = g;
        return { ...bag, stage: s, truckNo, weightLb: Number((bag.parcels * LB_PER_PARCEL).toFixed(1)) };
    };
    const truckNos = Array.from(new Set(scope.map((g) => g.truckNo).filter(Boolean))) as string[];
    const trucks: LoadDetailTruck[] = plan.trucks
        .filter((t) => truckNos.includes(t.truckNo))
        .map((t) => ({
            truckNo: t.truckNo,
            plate: t.plate,
            departedAt: t.departed ? capNow(b.createdAt + (2 + t.index) * HOUR, now) : undefined,
            bags: scope.filter((g) => g.truckNo === t.truckNo).map(toLoadBag),
        }));
    const looseBags = scope.filter((g) => !g.truckNo).map(toLoadBag);
    return {
        batch: b,
        stage,
        trucks,
        looseBags,
        totalBags: scope.length,
        totalParcels: scope.reduce((a, g) => a + g.parcels, 0),
    };
};

// ---------------------------------------------------------------- 号码归属

interface KnownBag {
    bag: BatchBag;
    /** 包牌原属批次 */
    batch: SortingBatch;
}

/** 本仓发出批次中可识别的包牌 */
export const findKnownBag = (state: SortingBatchMockState, number: string): KnownBag | undefined => {
    for (const b of state.outbound) {
        const hit = [...generatedBags(state, b), ...(state.extraBags[b.id] || [])].find((g) => g.bagNumber === number);
        if (hit) return { bag: hit, batch: b };
    }
    return undefined;
};

/** 原型 moveFlowOf：未识别号码按字符哈希分配流向（演示用） */
export const pseudoDestinationOf = (state: SortingBatchMockState, number: string, index: number): string => {
    const own = state.hubPrefix;
    const other = otherHubsOf(own)[0];
    const flows = [`${own}-SAN`, `${own}-SAN`, `${own}-LADC`, `${other}-NJ`, `${own}-LADC`, `${own}-SEA`, `${own}-MIA`, `${other}-NY`, `${other}-NJ`];
    let h = index;
    for (let k = 0; k < number.length; k += 1) h += number.charCodeAt(k);
    return flows[h % flows.length];
};

export const flowCovers = (batchDestination: string, destination: string): boolean =>
    batchDestination === destination || String(destination).indexOf(`${batchDestination}-`) === 0;

export interface MoveSource {
    batchNumber: string;
    destination: string;
    numbers: string[];
}

/**
 * 号码当前所在批次（原型 curBatchOf）：来源批次中的号码 → 来源批次；已移出 → 无；
 * 已识别包牌 → 最后一次移动的去向或原批次；其余按末位数字演示规则分配。
 */
export const currentBatchOfNumber = (
    state: SortingBatchMockState,
    number: string,
    destination: string,
    exclude?: string,
    source?: MoveSource | null,
): string | undefined => {
    if (source && source.numbers.includes(number)) return source.batchNumber;
    if (state.removedNumbers[number]) return undefined;
    const moves = state.bagMoves[number];
    if (moves && moves.length) {
        const to = moves[moves.length - 1].to;
        return to && to !== exclude ? to : undefined;
    }
    const known = findKnownBag(state, number);
    if (known) {
        if (state.movedOutBags[movedOutKey(known.batch.batchNumber, number)]) return undefined;
        return known.batch.batchNumber !== exclude ? known.batch.batchNumber : undefined;
    }
    const all = state.outbound.filter((b) => flowCovers(b.destination, destination) && b.batchNumber !== exclude
        && b.status !== 'CANCELLED' && (b.parcels || 0) > 0);
    const d = parseInt(String(number).slice(-1), 10);
    if (Number.isNaN(d) || [1, 3, 8].indexOf(d) < 0 || !all.length) return undefined;
    return all[d % all.length].batchNumber;
};

const destinationOfNumber = (state: SortingBatchMockState, number: string, index: number, source?: MoveSource | null): string => {
    if (source && source.numbers.includes(number)) return source.destination;
    const known = findKnownBag(state, number);
    if (known) return known.batch.destination;
    return pseudoDestinationOf(state, number, index);
};

export const parseMoveItems = (state: SortingBatchMockState, numbers: string[], source?: MoveSource | null): MoveInputItem[] =>
    numbers.map((number, i) => ({ number, isBag: isBagLikeNumber(number), destination: destinationOfNumber(state, number, i, source) }));

// ---------------------------------------------------------------- 移入

/** 移入解析（原型 moveGroups）：按流向分组并匹配候选批次 */
export const previewMoveIn = (state: SortingBatchMockState, numbers: string[], source?: MoveSource | null): MoveInPreview => {
    const items = parseMoveItems(state, numbers, source);
    const byFlow = new Map<string, MoveInputItem[]>();
    items.forEach((it) => byFlow.set(it.destination, [...(byFlow.get(it.destination) || []), it]));
    const groups: MoveInGroup[] = [];
    const orphans: MoveInOrphan[] = [];
    const src = source?.batchNumber;
    byFlow.forEach((flowItems, destination) => {
        const candidates = state.outbound
            .filter((b) => flowCovers(b.destination, destination) && !MOVE_IN_BLOCKED_STATUSES.includes(b.status) && b.batchNumber !== src)
            .sort((a, b) => (b.destination.length - a.destination.length) || (a.createdAt - b.createdAt));
        if (!candidates.length) {
            const blocked = state.outbound.filter((b) => flowCovers(b.destination, destination) && b.batchNumber !== src).length;
            const reason: MoveInOrphan['reason'] = blocked ? 'FLOW_BATCH_CLOSED' : (src ? 'NO_OTHER_SAME_FLOW' : 'NO_MATCHING_FLOW');
            flowItems.forEach((it) => orphans.push({
                ...it, reason, currentBatch: currentBatchOfNumber(state, it.number, destination, undefined, source),
            }));
            return;
        }
        groups.push({ destination, items: flowItems, candidates });
    });
    return { groups, orphans };
};

/** 目标批次中可接收该流向包裹的包牌：本会话新建 + 未使用 / 已使用的包牌（原型 moveBagsFor） */
export const moveBagsFor = (state: SortingBatchMockState, batch: SortingBatch, destination: string): BatchBag[] => {
    const fit = (code: string) => String(code).indexOf(destination) === 0 || flowCovers(code, destination);
    const made = (state.extraBags[batch.id] || []).filter((g) => g.createdForMove && fit(g.sortingCode));
    const usable = listBatchBags(state, batch).filter((g) => !g.createdForMove && (g.status === BagStatus.NOT_USED || g.status === BagStatus.USED) && fit(g.sortingCode));
    return [...made, ...usable];
};

export const moveBagsForCode = (state: SortingBatchMockState, batch: SortingBatch, destination: string, code: string): BatchBag[] =>
    moveBagsFor(state, batch, destination).filter((g) => g.sortingCode === code || String(code).indexOf(`${g.sortingCode}-`) === 0);

/** 包裹的分拣码（原型 movePkgCode：优先能匹配到可用包牌的四段码） */
export const parcelSortingCode = (state: SortingBatchMockState, batch: SortingBatch, destination: string, number: string): string => {
    const all = codesAt(batch.destination, 4, state.sortCodes).filter((c) => String(c).indexOf(destination) === 0);
    if (!all.length) return destination;
    const bags = moveBagsFor(state, batch, destination);
    const ok = all.filter((c) => bags.some((g) => g.sortingCode === c || String(c).indexOf(`${g.sortingCode}-`) === 0));
    const pool = ok.length ? ok : all;
    let h = 0;
    for (let k = 0; k < number.length; k += 1) h += number.charCodeAt(k) * (k + 1);
    return pool[h % Math.min(pool.length, 2)];
};

/** 包裹按分拣码分组（原型 movePkgGroups） */
export const groupParcelsByCode = (
    state: SortingBatchMockState,
    batch: SortingBatch,
    destination: string,
    items: MoveInputItem[],
): { code: string; numbers: string[] }[] => {
    const by = new Map<string, string[]>();
    items.filter((i) => !i.isBag).forEach((it) => {
        const code = parcelSortingCode(state, batch, destination, it.number);
        by.set(code, [...(by.get(code) || []), it.number]);
    });
    return Array.from(by.keys()).sort().map((code) => ({ code, numbers: by.get(code) || [] }));
};

// ---------------------------------------------------------------- 移出

/** 移出解析（原型 outItems） */
export const previewMoveOut = (state: SortingBatchMockState, numbers: string[]): MoveOutItem[] =>
    numbers.map((number, i) => {
        const isBag = isBagLikeNumber(number);
        const destination = destinationOfNumber(state, number, i);
        const currentBatch = currentBatchOfNumber(state, number, destination);
        const b = currentBatch ? findBatchByNumber(state, currentBatch) : undefined;
        let rejectReason: MoveOutItem['rejectReason'];
        if (!currentBatch) rejectReason = 'NOT_IN_ANY_BATCH';
        else if (b && b.status === 'COMPLETED') rejectReason = 'BATCH_COMPLETED';
        return { number, isBag, destination, currentBatch, rejectReason };
    });

/** 批次是否已结束分拣或终态 */
export const isClosedBatch = (b: SortingBatch): boolean => CLOSED_STATUSES.includes(b.status);
